import { getDataForChart } from '../../../utils/reactChartConfig'
import { CONSTRUCTS } from '../../../utils/constructs'
import { convert1to6ToPercent } from '../../../utils/statisticCalc'

/**
 * called by ResultsModule1.js and YearlyComparison.js
 * @param jtLabels, object, 'label' portion of the jsonText object from Redux
 * @param modNpoAnswers, object, latest NPO survey answers for current module
 * @param benchMod, object, benchmark of either this NPO category or of all NPOs
 * @param modResults, object, the latest results of the user's organization with respect to above benchmark (percentiles)
 * @param benchType, string, benchmark type, either 'cat' or 'all'
 * @param yearlyComp, object, containing all the info and data for a yearly comparison (or null)
 * @param chartType, string, either 'summary' (overall result) or the section name ('activation' or 'future orientation')
 * @return object, the chart data for Module 1
 *
 * Note: for yearly comparison, we use the following variables slightly differently:
 * benchMod1All = benchmark, either for all NPOs ('all') or only the NPOs of the same category ('cat')
 * modBenchCatResults = last survey results
 * mod1AllResults = results of selected year's survey (or null)
 * mod1CatResults = always null
 */
export const getChartDataMod1 = (jtLabels, modNpoAnswers, benchMod, modResults, benchType, yearlyComp, chartType) => {
  // console.log('modBenchCatResults:', modBenchCatResults)
  if (yearlyComp) {
    modNpoAnswers = yearlyComp.benchmark
    benchMod = yearlyComp.lastSurveyResults
    modResults = yearlyComp.selectedSurveyResults
    benchType = null
  }
  let chartLabels, npoData, benchData, resultsData
  switch (chartType) {
    case 'summary':
      chartLabels = [  // the order here pertains only to the chart
        `${jtLabels[CONSTRUCTS.ENTREPRENEURIAL_ORIENTATION.name] || 'Entrepreneurial Orientation'}`.toUpperCase(),
        `${jtLabels[CONSTRUCTS.ACTIVATION.name] || 'Activation'}`.toUpperCase(),
        `${jtLabels[CONSTRUCTS.FUTURE_ORIENTATION.name] || 'Future Orientation'}`.toUpperCase(),
      ]
      npoData = [
        modNpoAnswers?.UFK.mean,
        modNpoAnswers?.AKT.mean,
        modNpoAnswers?.ZKO.mean,
      ]
      benchData = [
        benchMod?.UFK.mean,
        benchMod?.AKT.mean,
        benchMod?.ZKO.mean,
      ]
      if (yearlyComp) resultsData = [
        modResults?.UFK.percentile,
        modResults?.AKT.percentile,
        modResults?.ZKO.percentile,
      ]
      break
    case CONSTRUCTS.ACTIVATION.name:
      chartLabels = [
        `${jtLabels[CONSTRUCTS.AUTONOMY.name] || 'Autonomy'}`.toUpperCase(),
        `${jtLabels[CONSTRUCTS.COMMUNITY_MOBILIZATION.name] || 'Community Mobilization'}`.toUpperCase(),
        `${jtLabels[CONSTRUCTS.COOPERATIVE_MOBILIZATION.name] || 'Cooperative Mobilization'}`.toUpperCase(),
        `${jtLabels[CONSTRUCTS.AGGRESSIVENESS.name] || 'Aggressiveness'}`.toUpperCase(),
      ]
      npoData = [
        modNpoAnswers?.AUT.mean,
        modNpoAnswers?.GEM.mean,
        modNpoAnswers?.KOP.mean,
        modNpoAnswers?.AGR.mean,
      ]
      benchData = [
        benchMod?.AUT.mean,
        benchMod?.GEM.mean,
        benchMod?.KOP.mean,
        benchMod?.AGR.mean,
      ]
      if (yearlyComp) resultsData = [
        modResults?.AUT.percentile,
        modResults?.GEM.percentile,
        modResults?.KOP.percentile,
        modResults?.AGR.percentile,
      ]
      break
    case CONSTRUCTS.FUTURE_ORIENTATION.name:
      chartLabels = [
        `${jtLabels[CONSTRUCTS.PROACTIVITY.name] || 'Proactivity'}`.toUpperCase(),
        `${jtLabels[CONSTRUCTS.INNOVATION.name] || 'Innovation'}`.toUpperCase(),
        `${jtLabels[CONSTRUCTS.RISK_TAKING.name] || 'Risk-taking'}`.toUpperCase(),
      ]
      npoData = [
        modNpoAnswers?.PRO.mean,
        modNpoAnswers?.INN.mean,
        modNpoAnswers?.RSK.mean,
      ]
      benchData = [
        benchMod?.PRO.mean,
        benchMod?.INN.mean,
        benchMod?.RSK.mean,
      ]
      if (yearlyComp) resultsData = [
        modResults?.PRO.percentile,
        modResults?.INN.percentile,
        modResults?.RSK.percentile,
      ]
      break
    default:
      console.error('No match found for chart type:', chartType)
  }

  return getDataForChart(jtLabels, chartLabels, npoData, benchData, resultsData, benchType, yearlyComp)
}
